import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FormattedMessage } from 'react-intl';

import Modal from 'react-modal';

import { useProjectCollaborationDelete } from 'components/projects/collaboration/hooks';
import { Button } from '@companycam/slab-web';

const CollabColor = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin: 0 6px 0 4px;
  background-color: ${(props) => props.collabColor};
  vertical-align: middle;
`;

const CollabColorNameLayout = styled.span`
  white-space: nowrap;
`;

const EndCollaborationConfirmModal = ({
  color,
  collaborationId,
  handleNavigation,
  isOwningCollaborator,
  name,
  onRequestClose,
  owner,
  projectNodeId,
}) => {
  const { deleteProjectCollaboration, loading } = useProjectCollaborationDelete(
    {
      collaborationId,
      projectNodeId,
      onSuccess: useCallback(() => {
        onRequestClose();
        handleNavigation();
      }, [onRequestClose, handleNavigation]),
    },
  );

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      contentLabel="End project collaboration confirmation"
      overlayClassName="modal"
      className="modal-content-wrap"
    >
      <h3 className="modal-title">
        <FormattedMessage
          id="Projects.collaboration.endCollaborationConfirmModal.modalTitle"
          defaultMessage="Are you sure?"
          description="modal title"
        />
      </h3>
      <form>
        <p className="instructions">
          <FormattedMessage
            id="Projects.collaboration.endCollaborationConfirmModal.modalInstructions"
            defaultMessage="If you end the collaboration with"
            description="message to the user asking them to confirm their current action"
          />
          <CollabColorNameLayout>
            {color ? (
              <CollabColor aria-hidden="true" collabColor={color} />
            ) : null}
            {isOwningCollaborator ? name : owner}
            {', '}
          </CollabColorNameLayout>
          {isOwningCollaborator ? (
            <FormattedMessage
              id="Projects.collaboration.endCollaborationConfirmModal.modalWarning"
              defaultMessage="they won't be able to see any ongoing updates to this project unless you re-invite them."
              description="message to the user warning them that the action cannot be undone"
            />
          ) : (
            <FormattedMessage
              id="Projects.collaboration.endCollaborationConfirmModal.modalCollaboratorWarning"
              defaultMessage="you won't be able to see any ongoing updates to this project unless they re-invite you."
              description="message to the user warning them that the action cannot be undone"
            />
          )}
        </p>
        <div className="btn-container full">
          <Button
            color="destroy"
            disabled={loading}
            loading={loading}
            onClick={deleteProjectCollaboration}
            style={{ marginRight: 8 }}
          >
            {loading ? (
              <FormattedMessage
                id="Projects.collaboration.endCollaborationConfirmModal.endingMessage"
                defaultMessage="Ending Collaboration..."
              />
            ) : (
              <FormattedMessage
                id="Projects.collaboration.endCollaborationConfirmModal.confirm"
                defaultMessage="Yes, End Collaboration"
              />
            )}
          </Button>
          <Button onClick={onRequestClose}>
            <FormattedMessage
              id="Projects.collaboration.endCollaborationConfirmModal.cancel"
              defaultMessage="Nevermind"
            />
          </Button>
        </div>
      </form>
    </Modal>
  );
};

EndCollaborationConfirmModal.propTypes = {
  color: PropTypes.string,
  collaborationId: PropTypes.string,
  handleNavigation: PropTypes.func,
  isOwningCollaborator: PropTypes.bool,
  name: PropTypes.string,
  onRequestClose: PropTypes.func,
  owner: PropTypes.string,
  projectNodeId: PropTypes.string,
};

export default EndCollaborationConfirmModal;
